package com.evoapps.evoshift.observability

/** How the person signed in or up. */
enum class AuthMethod(val id: String) { EMAIL("email"), GOOGLE("google"), APPLE("apple") }

/**
 * The whole catalogue of usage events (SH-86): what happened, never who or how much. Parameters
 * are fixed identifiers (a method, a plan id, a help page, a screen), never names, e-mails,
 * free text or amounts; [sanitize] drops anything else before it reaches the SDK.
 */
sealed class AnalyticsEvent(val name: String, val params: Map<String, String> = emptyMap()) {
    class Login(method: AuthMethod) : AnalyticsEvent("login", mapOf(PARAM_METHOD to method.id))
    class SignUp(method: AuthMethod) : AnalyticsEvent("sign_up", mapOf(PARAM_METHOD to method.id))
    data object WorkplaceCreated : AnalyticsEvent("workplace_created")
    data object RosterCreated : AnalyticsEvent("roster_created")
    data object RosterPublished : AnalyticsEvent("roster_published")
    data object ShiftSwapRequested : AnalyticsEvent("shift_swap_requested")
    data object ShiftTaken : AnalyticsEvent("shift_taken")
    data object AvailabilitySubmitted : AnalyticsEvent("availability_submitted")
    data object DailyResultEntered : AnalyticsEvent("daily_result_entered")
    /** A one-off expense added to a month (SH-84): that it happened, not the amount. */
    data object OneOffCostAdded : AnalyticsEvent("one_off_cost_added")
    /** A one-off revenue item added to a month (SH-87): that it happened, not the amount. */
    data object OneOffRevenueAdded : AnalyticsEvent("one_off_revenue_added")
    /** A recurring rule saved as revenue (SH-87): no name or amount. */
    data object RecurringRevenueAdded : AnalyticsEvent("recurring_revenue_added")
    /** A bonus, deduction or expense refund added to a person's month (SH-78): no kind, person or amount. */
    data object AdjustmentAdded : AnalyticsEvent("adjustment_added")
    /** Medical exam / OHS dates recorded for someone (SH-47): that it happened, never whose or which dates. */
    data object StaffChecksSaved : AnalyticsEvent("staff_checks_saved")
    /** A month's capacity opened (SH-48): no month, range, people or hours. */
    data object CapacityViewed : AnalyticsEvent("capacity_viewed")
    /** People reminded to send their availability (SH-48): not who or how many. */
    data object AvailabilityReminderSent : AnalyticsEvent("availability_reminder_sent")
    /** Planned times copied into the actual ones on Daily Records (SH-46): no day, person or count. */
    data object PlannedTimesCopied : AnalyticsEvent("planned_times_copied")
    /** Proposals of auto-assign written to a month (SH-53): that it happened, not how many or to whom. */
    data object ShiftsAutoAssigned : AnalyticsEvent("shifts_auto_assigned")
    /** Shifts copied into a month from another week or month (SH-114): that it happened, not how many or whose. */
    data object ShiftsCopied : AnalyticsEvent("shifts_copied")
    /** AI suggestions asked for in auto-assign (SH-90): whether they were used or the rules' plan was shown instead; no counts, people or month. */
    class AiShiftSuggestions(fellBack: Boolean) : AnalyticsEvent(
        "ai_shift_suggestions",
        mapOf(PARAM_OUTCOME to if (fellBack) "rules_fallback" else "ai")
    )
    /** A month e-mailed (SH-45): to everyone or one person, the month or some days; no person, roster or count. */
    class RosterEmailed(everyone: Boolean, wholeMonth: Boolean) : AnalyticsEvent(
        "roster_emailed",
        mapOf(PARAM_SCOPE to if (everyone) "all" else "one", PARAM_PERIOD to if (wholeMonth) "month" else "days")
    )
    /** Own shifts saved as an .ics file (SH-45). */
    data object CalendarExported : AnalyticsEvent("calendar_exported")
    /** The private calendar link created (SH-45). */
    data object CalendarLinkCreated : AnalyticsEvent("calendar_link_created")
    /** The private calendar link turned off (SH-45). */
    data object CalendarLinkRevoked : AnalyticsEvent("calendar_link_revoked")
    /** A calendar app's add-a-subscription page opened, or the link copied (SH-202): `apple`, `google`, `outlook`, `outlook_work`, `copy`. */
    class CalendarAppOpened(target: String) : AnalyticsEvent("calendar_app_opened", mapOf(PARAM_TARGET to target))
    /**
     * SH-219: a new owner's first month published on the start path: how long it took from the
     * path's start, as a coarse bucket ([com.evoapps.evoshift.domain.usecase.start.FirstPublishTime],
     * e.g. `under_10_min`), sent once per workplace; no workplace, month or exact time.
     */
    class FirstRosterPublished(duration: String) : AnalyticsEvent("first_roster_published", mapOf(PARAM_DURATION to duration))
    data object PaywallViewed : AnalyticsEvent("paywall_viewed")
    /** A plan bought: its store product id only (e.g. `evoshift_premium:seats_5`), no price. */
    class Purchase(planId: String) : AnalyticsEvent("purchase", mapOf(PARAM_PLAN_ID to planId))
    /** A help-centre page opened, by its slug (e.g. `rosters`). */
    class HelpOpened(page: String) : AnalyticsEvent("help_opened", mapOf(PARAM_PAGE to page))
    /** A screen shown, by its route's name (e.g. `RosterDetail`), not its arguments. */
    class ScreenView(screen: String) : AnalyticsEvent(
        "screen_view",
        mapOf(PARAM_SCREEN_NAME to screen, PARAM_SCREEN_CLASS to screen)
    )

    override fun toString(): String = "$name$params"

    companion object {
        const val PARAM_METHOD = "method"
        const val PARAM_PLAN_ID = "plan_id"
        const val PARAM_PAGE = "page"
        const val PARAM_SCOPE = "scope"
        const val PARAM_PERIOD = "period"
        const val PARAM_OUTCOME = "outcome"
        const val PARAM_TARGET = "target"
        const val PARAM_DURATION = "duration"
        // Firebase's own names for screen views.
        const val PARAM_SCREEN_NAME = "screen_name"
        const val PARAM_SCREEN_CLASS = "screen_class"

        /** The only parameter names that may leave the app. */
        val ALLOWED_PARAMS = setOf(PARAM_METHOD, PARAM_PLAN_ID, PARAM_PAGE, PARAM_SCOPE, PARAM_PERIOD, PARAM_OUTCOME, PARAM_TARGET, PARAM_DURATION, PARAM_SCREEN_NAME, PARAM_SCREEN_CLASS)

        /**
         * An identifier, not prose: letters, digits and `_ . : -`, at most 100 characters (Firebase's
         * limit). No `@` (e-mails) and no spaces (names, sentences).
         */
        val SAFE_VALUE = Regex("^[A-Za-z0-9_.:-]{1,100}$")

        /** Event names: Firebase's rule (a letter, then letters, digits and `_`, at most 40). */
        val SAFE_NAME = Regex("^[a-z][a-z0-9_]{0,39}$")

        /** [params] without anything outside [ALLOWED_PARAMS] or not an identifier ([SAFE_VALUE]). */
        fun sanitize(params: Map<String, String>): Map<String, String> =
            params.filter { (key, value) -> key in ALLOWED_PARAMS && SAFE_VALUE.matches(value) }
    }
}
